import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import {
  createShareableProfileSettings,
  getShareableProfileSettings,
  getApproachability,
  getProfileVisibility,
  updateApproachabilityMutation,
  updateProfileVisibilityMutation,
  updateShareableProfileSettings,
} from './visibility.graphql';
import { useMutation, useQuery } from '@apollo/client';
import type {
  CreateShareableProfileSettingsMutation,
  GetShareableProfileSettingsQuery,
  GetApproachabilityQuery,
  GetProfileVisibilityQuery,
  UpdateApproachabilityMutation,
  UpdateProfileVisibilityMutation,
  UpdateShareableProfileSettingsMutation,
} from 'src/types/graphql';
import { useCallback } from 'react';
import { useToast } from 'braid-design-system';
import { useTypedSelector } from 'src/reducers';
import { useMelwaysInfo } from '@seek/melways-react';

/*
Talent Search visibility:
*/
export const useGetProfileVisibility = () => {
  const { language } = useMelwaysInfo();
  const { data, loading, error } = useQuery<GetProfileVisibilityQuery>(
    getProfileVisibility,
    {
      variables: {
        languageCode: language,
      },
    },
  );

  const profileVisibilityLevel =
    loading || error ? null : data?.viewer?.profileVisibility?.level || null;
  const profileVisibilityDescription =
    loading || error
      ? null
      : data?.viewer?.profileVisibility?.description || null;

  return {
    profileVisibilityLevel,
    profileVisibilityDescription,
    loading,
    error,
  };
};

export const useUpdateProfileVisibility = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [updateProfileVisibility, { data, loading, error }] =
    useMutation<UpdateProfileVisibilityMutation>(
      updateProfileVisibilityMutation,
      {
        onCompleted,
        onError,
      },
    );

  return { updateProfileVisibility, data, loading, error };
};

export const useGetApproachability = () => {
  const { data, loading, error } =
    useQuery<GetApproachabilityQuery>(getApproachability);

  const approachable = data?.viewer?.approachability?.approachable ?? true;

  return {
    approachable,
    loading,
    error,
  };
};

export const useUpdateApproachability = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [updateApproachability, { data, loading, error }] =
    useMutation<UpdateApproachabilityMutation>(updateApproachabilityMutation, {
      onCompleted,
      onError,
    });

  return { updateApproachability, data, loading, error };
};

/*
Shared Profile visibility:
*/
export const useGetSharedProfileVisibility = () => {
  const { language } = useMelwaysInfo();
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);

  const { data, loading, error } = useQuery<GetShareableProfileSettingsQuery>(
    getShareableProfileSettings,
    {
      variables: {
        languageCode: language,
        zone,
      },
    },
  );

  const shareableProfileSettings = data?.viewer?.shareableProfileSettings;

  const isShareableProfileActive =
    loading || error ? false : shareableProfileSettings?.active || false;

  // const shareableProfileViewCount =
  //   loading || error ? null : shareableProfileSettings?.views || 0;

  const shareableProfileUrl =
    loading || error ? null : shareableProfileSettings?.url || null;

  const shareableProfileDisplayUrl =
    loading || error ? null : shareableProfileSettings?.displayUrl || null;

  return {
    isShareableProfileActive,
    shareableProfileUrl,
    shareableProfileDisplayUrl,
    // shareableProfileViewCount,
    loading,
    error,
  };
};

export const useUpdateSharedProfileVisibility = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: 'Shared profile setting could not be updated',
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast]);

  const [updateSharedProfileVisibility, { data, loading, error }] =
    useMutation<UpdateShareableProfileSettingsMutation>(
      updateShareableProfileSettings,
      {
        onCompleted,
        onError,
      },
    );

  return { updateSharedProfileVisibility, data, loading, error };
};

export const useCreateSharedProfileUrl = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = (data: CreateShareableProfileSettingsMutation) => {
    if ('viewer' in data?.createShareableProfileSettings) {
      const profileUrl =
        data?.createShareableProfileSettings?.viewer?.shareableProfileSettings
          ?.url;
      if (profileUrl) {
        navigator.clipboard.writeText(profileUrl);
      }

      showToast({
        message: t('Profile visibility updated. Link copied'),
        tone: 'positive',
        key: 'profile-visibility',
      });
    }
  };

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [createShareableProfileUrl, { data, loading, error }] =
    useMutation<CreateShareableProfileSettingsMutation>(
      createShareableProfileSettings,
      {
        onCompleted,
        onError,
      },
    );

  return { createShareableProfileUrl, data, loading, error };
};
